import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { useReportFilters } from "../shared/useReportFilters";
import { pad, toISO } from "../shared/utils";
import { API_URL, RP_CHART_MONO } from "../shared/constants";

export function useMachineProductionReport() {
  const { getToken } = useAuth();
  const filters = useReportFilters();

  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [prodData, setProdData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("day");
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );
  const addToast = useCallback(
    (msg, type = "success") =>
      setToasts((p) => [...p, { id: Date.now(), message: msg, type }]),
    [],
  );
  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
      } catch {}
    })();
  }, [authH]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("from", toISO(filters.startDate, false));
      p.set("to", toISO(filters.endDate, true));
      if (filters.machineIds.length)
        p.set("machineId", filters.machineIds.join(","));
      if (filters.shiftId) p.set("shiftId", filters.shiftId);
      p.set("groupBy", groupBy);
      const r = await axios.get(
        `${API_URL}/api/reports/production-by-date?${p}`,
        authH(),
      );
      setProdData(r.data.data);
    } catch {
      addToast("Makine üretim verisi yüklenemedi", "danger");
      setProdData(null);
    } finally {
      setLoading(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.machineIds,
    filters.shiftId,
    groupBy,
    authH,
    addToast,
  ]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line
  useEffect(() => {
    fetchData();
  }, [groupBy]); // eslint-disable-line

  const filtersKey = `${filters.startDate}|${filters.endDate}|${filters.machineIds.join(",")}|${filters.shiftId}`;
  useEffect(() => {
    fetchData();
  }, [filtersKey]); // eslint-disable-line

  const derived = useMemo(() => {
    if (!prodData) return null;
    const { records = [] } = prodData;

    const dateKeys = [
      ...new Set(
        records.map(
          (r) =>
            `${r.date.year}-${pad(r.date.month || 1)}-${pad(r.date.day || 1)}`,
        ),
      ),
    ].sort();

    const machineNames = [...new Set(records.map((r) => r.machineName))];
    const seriesByMachine = machineNames.map((mn) => ({
      name: mn,
      data: dateKeys.map((dk) =>
        records
          .filter(
            (r) =>
              `${r.date.year}-${pad(r.date.month || 1)}-${pad(r.date.day || 1)}` ===
                dk && r.machineName === mn,
          )
          .reduce((s, r) => s + r.quantity, 0),
      ),
    }));

    const totalPerDate = dateKeys.map((dk) =>
      records
        .filter(
          (r) =>
            `${r.date.year}-${pad(r.date.month || 1)}-${pad(r.date.day || 1)}` ===
            dk,
        )
        .reduce((s, r) => s + r.quantity, 0),
    );

    const movingAvg =
      groupBy === "day"
        ? totalPerDate.map((_, i) => {
            const w = totalPerDate.slice(Math.max(0, i - 2), i + 3);
            return Math.round(w.reduce((s, v) => s + v, 0) / w.length);
          })
        : [];

    const machineTotals = machineNames
      .map((mn) => ({
        name: mn,
        total: records
          .filter((r) => r.machineName === mn)
          .reduce((s, r) => s + r.quantity, 0),
      }))
      .sort((a, b) => b.total - a.total);

    return {
      dateKeys,
      machineNames,
      seriesByMachine,
      totalPerDate,
      movingAvg,
      machineTotals,
    };
  }, [prodData, groupBy]);

  return {
    filters,
    machines,
    shifts,
    loading,
    derived,
    groupBy,
    setGroupBy,
    fetchData,
    toasts,
    dismissToast,
    chartColor: RP_CHART_MONO,
  };
}
